import { HUDDLE_AUDIO_CHUNK_MS, IHuddleAudioChunk } from '@common/interfaces/huddles'

/** A level this loud at any sample means someone spoke; a quieter stretch isn't sent. */
const SPEECH_LEVEL = 0.06
const LEVEL_SAMPLE_MS = 250

// Re-exported for huddleSession, which imports it from here.
export type { IHuddleAudioChunk }

export interface IHuddleRecorderDeps {
  /** This browser's own voice, after the mute: muted stretches record silence. */
  voice: MediaStream
  /** The same voice's level, 0–1. */
  getLevel: () => number
  onChunk: (chunk: IHuddleAudioChunk) => void
  container: { mimeType: string; extension: string }
  chunkMs?: number
}

interface IStretch {
  recorder: MediaRecorder
  parts: Blob[]
  startedAt: number
  hadSpeech: boolean
}

/**
 * Records this browser's own voice in stretches for the huddle transcript. Each stretch
 * is its own file (a recorder restarted, not a timeslice, whose later pieces aren't
 * playable alone), and one nobody spoke in is never sent.
 */
export class HuddleRecorder {
  private current: IStretch | null = null

  private readonly timers: ReturnType<typeof setInterval>[] = []

  private isStopped = false

  constructor(private readonly deps: IHuddleRecorderDeps) {
    this.startStretch()
    this.timers.push(
      setInterval(() => this.startStretch(), deps.chunkMs ?? HUDDLE_AUDIO_CHUNK_MS),
      setInterval(() => {
        if (this.current && this.deps.getLevel() >= SPEECH_LEVEL) this.current.hadSpeech = true
      }, LEVEL_SAMPLE_MS),
    )
  }

  /** Send what's recorded so far and stop. */
  stop(): void {
    if (this.isStopped) return
    this.isStopped = true
    this.timers.forEach((timer) => clearInterval(timer))
    this.current?.recorder.stop()
    this.current = null
  }

  /** Close the stretch running, if any, and open the next. */
  private startStretch(): void {
    this.current?.recorder.stop()
    const { voice, container } = this.deps
    const stretch: IStretch = {
      recorder: new MediaRecorder(voice, { mimeType: container.mimeType }),
      parts: [],
      startedAt: Date.now(),
      hadSpeech: false,
    }
    stretch.recorder.ondataavailable = ({ data }) => {
      if (data.size > 0) stretch.parts.push(data)
    }
    stretch.recorder.onstop = () => this.finishStretch(stretch)
    this.current = stretch
    stretch.recorder.start()
  }

  private finishStretch({ parts, startedAt, hadSpeech }: IStretch): void {
    if (!hadSpeech || parts.length === 0) return
    const { mimeType, extension } = this.deps.container
    const file = new File(parts, `huddle-${startedAt}.${extension}`, { type: mimeType })
    this.deps.onChunk({ file, startedAt: new Date(startedAt).toISOString() })
  }
}
